import { useEffect, useMemo, useState } from "react";
import {
  Coins, Save, RotateCcw, Loader2, User2, Wallet, TrendingUp,
  Info, PackageCheck,
} from "lucide-react";
import { toast } from "sonner";
import { Routes as RoutesAPI } from "../lib/api";
import { PageSkeleton } from "../components/PageSkeleton";

export default function DriverRatesPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [drafts, setDrafts] = useState({}); // uid → string

  const load = () => {
    setLoading(true);
    RoutesAPI.listDriverRates()
      .then((d) => {
        setData(d);
        setDrafts(
          Object.fromEntries(
            (d.drivers || []).map((dr) => [
              dr.user_id,
              dr.custom_package_rate != null ? String(dr.custom_package_rate) : "",
            ]),
          ),
        );
      })
      .catch(() => toast.error("Nie mogę pobrać stawek"))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const save = async (uid) => {
    const raw = drafts[uid];
    const rate = raw === "" || raw == null ? null : Number(raw);
    if (raw !== "" && (Number.isNaN(rate) || rate < 0)) {
      toast.error("Podaj poprawną liczbę ≥ 0 lub zostaw puste (fallback do środowiska)");
      return;
    }
    try {
      await RoutesAPI.setDriverRate(uid, rate);
      toast.success(rate === null
        ? "Stawka wyzerowana — fallback do stawki globalnej"
        : `Stawka ustawiona: ${rate} PLN/paczka`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Błąd zapisu");
    }
  };

  const clear = async (uid) => {
    try {
      await RoutesAPI.setDriverRate(uid, null);
      toast.success("Wyzerowano — fallback do stawki globalnej");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Błąd");
    }
  };

  const totals = useMemo(() => {
    if (!data?.drivers) return { total: 0, custom: 0, wallet: 0 };
    return data.drivers.reduce((acc, d) => {
      acc.total += 1;
      if (d.custom_package_rate != null) acc.custom += 1;
      acc.wallet += Number(d.wallet_balance || 0);
      return acc;
    }, { total: 0, custom: 0, wallet: 0 });
  }, [data]);

  if (loading) {
    return (
      <PageSkeleton label="Ładuję stawki…" />
    );
  }

  return (
    <div className="p-3 md:p-6 space-y-5" data-testid="driver-rates-page">
      {/* Hero */}
      <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-emerald-600 via-teal-800 to-zinc-950 text-white p-6 md:p-8 ring-1 ring-white/10 shadow-[0_25px_60px_-25px_rgba(0,0,0,0.7)]">
        <Coins className="absolute -right-10 -bottom-16 w-72 h-72 opacity-[0.10]" strokeWidth={1.5} />
        <div className="relative flex items-center gap-2">
          <div className="w-10 h-10 rounded-lg bg-white/15 flex items-center justify-center ring-1 ring-white/25">
            <Coins className="w-5 h-5" />
          </div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-white/70">
            // Panel administratora · Stawki kurierów
          </div>
        </div>
        <h1 className="relative mt-3 font-heading font-black text-3xl md:text-5xl uppercase tracking-tight">
          Stawki kurierów
        </h1>
        <div className="relative mt-1 text-white/80 max-w-2xl text-sm">
          Ustaw indywidualną stawkę PLN za doręczoną paczkę dla każdego kuriera.
          Pusto = fallback do stawki globalnej ({data?.env_rate} PLN).
          Portfel: <b>1× pełna stawka za pierwszą paczkę na adres</b>{" "}
          + <b>{Number(data?.multipack_extra_rate ?? 0.5).toFixed(2)} PLN</b> za każdą kolejną
          (multipack).
        </div>

        <div className="relative mt-5 flex gap-3 flex-wrap">
          <StatChip label="Wszyscy kurierzy" value={totals.total} tone="cyan" />
          <StatChip label="Ze stawką indywidualną" value={totals.custom} tone="amber" />
          <StatChip
            label="Suma portfeli (PLN)"
            value={totals.wallet.toLocaleString("pl-PL", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            tone="emerald"
          />
        </div>
      </div>

      {/* Table */}
      <div className="rounded-2xl bg-white ring-1 ring-zinc-200 overflow-hidden shadow-sm">
        <div className="px-4 py-3 border-b border-zinc-100 bg-zinc-50 flex items-center gap-2">
          <User2 className="w-4 h-4 text-zinc-500" />
          <div className="text-sm font-bold text-zinc-800">Lista kurierów</div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 ml-auto">
            {data?.drivers?.length || 0} użytkowników
          </div>
        </div>

        {(!data?.drivers?.length) ? (
          <div className="p-12 text-center text-sm text-zinc-500">Brak kurierów w bazie.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 text-[10px] font-mono uppercase tracking-widest text-zinc-500">
              <tr>
                <th className="text-left px-4 py-2">Kurier</th>
                <th className="text-right px-4 py-2">Doręczone</th>
                <th className="text-right px-4 py-2">Awizo</th>
                <th className="text-center px-4 py-2">Stawka indywidualna</th>
                <th className="text-right px-4 py-2">Efektywna stawka</th>
                <th className="text-right px-4 py-2">Portfel</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {data.drivers.map((d) => {
                const draft = drafts[d.user_id] ?? "";
                const draftNum = draft === "" ? null : Number(draft);
                const isDirty =
                  draft !== (d.custom_package_rate != null ? String(d.custom_package_rate) : "");
                const projected =
                  draftNum != null && !Number.isNaN(draftNum)
                    ? draftNum
                    : data.env_rate;
                const extra = Number(d.approved_multipack_extra_count || 0);
                const full = Math.max(
                  Number(d.approved_delivered_count || 0) - extra, 0,
                );
                const extraRate = Number(data.multipack_extra_rate ?? 0.5);
                const projectedWallet = (full * projected + extra * extraRate).toFixed(2);

                return (
                  <tr key={d.user_id} className="border-t border-zinc-100 hover:bg-zinc-50"
                      data-testid={`rate-row-${d.user_id}`}>
                    <td className="px-4 py-3">
                      <div className="font-semibold">{d.name || d.email}</div>
                      <div className="text-[10px] font-mono text-zinc-500">
                        {d.email || d.username || d.user_id}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right font-mono">
                      <span className="inline-flex items-center gap-1 text-emerald-700">
                        <PackageCheck className="w-3 h-3" /> {d.approved_delivered_count}
                      </span>
                      {extra > 0 && (
                        <div className="text-[10px] font-mono text-violet-700 mt-0.5"
                             title={`${extra} paczek dodatkowych w multipackach × ${extraRate.toFixed(2)} zł`}>
                          + {extra} multipack
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right font-mono text-amber-700">
                      {d.approved_awizo_count}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-center gap-1">
                        <input
                          type="number"
                          min={0}
                          step="0.5"
                          placeholder={`${data.env_rate}`}
                          value={draft}
                          onChange={(e) =>
                            setDrafts({ ...drafts, [d.user_id]: e.target.value })
                          }
                          data-testid={`rate-input-${d.user_id}`}
                          className="w-24 px-2 py-1.5 rounded-lg border border-zinc-300 text-sm text-right font-mono outline-none focus:ring-2 focus:ring-emerald-300"
                        />
                        <span className="text-[10px] font-mono text-zinc-500">PLN</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right font-heading font-black">
                      <span className={d.custom_package_rate != null ? "text-emerald-700" : "text-zinc-400"}>
                        {d.effective_rate.toFixed(2)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex items-center gap-1 text-emerald-700 font-heading font-black">
                        <Wallet className="w-3.5 h-3.5" />
                        {d.wallet_balance.toLocaleString("pl-PL", {
                          minimumFractionDigits: 2, maximumFractionDigits: 2,
                        })}
                      </div>
                      {isDirty && !Number.isNaN(projectedWallet) && (
                        <div className="text-[10px] font-mono text-amber-600 mt-0.5"
                             title="Nowy portfel po zapisie">
                          <TrendingUp className="w-3 h-3 inline" /> →{" "}
                          {Number(projectedWallet).toLocaleString("pl-PL", {
                            minimumFractionDigits: 2, maximumFractionDigits: 2,
                          })}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <button
                        onClick={() => save(d.user_id)}
                        disabled={!isDirty}
                        data-testid={`btn-save-rate-${d.user_id}`}
                        className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-bold uppercase tracking-widest transition-colors ${
                          isDirty
                            ? "bg-zinc-900 text-white hover:bg-brand"
                            : "bg-zinc-100 text-zinc-400 cursor-not-allowed"
                        }`}
                      >
                        <Save className="w-3.5 h-3.5" /> Zapisz
                      </button>
                      {d.custom_package_rate != null && (
                        <button
                          onClick={() => clear(d.user_id)}
                          data-testid={`btn-clear-rate-${d.user_id}`}
                          className="ml-1 p-1.5 rounded-lg hover:bg-zinc-100 text-zinc-500"
                          title="Wyzeruj (fallback do stawki globalnej)"
                        >
                          <RotateCcw className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}

        <div className="px-4 py-3 border-t border-zinc-100 bg-zinc-50 text-[11px] text-zinc-600 flex items-center gap-2">
          <Info className="w-3.5 h-3.5" />
          Pusta wartość = użyta zostaje globalna stawka <b className="font-mono">{data?.env_rate}</b> PLN
          (env <code>PACKAGE_RATE_PLN</code>). Portfel = pełne × stawka + multipack ×{" "}
          <b className="font-mono">{Number(data?.multipack_extra_rate ?? 0.5).toFixed(2)}</b> PLN.
        </div>
      </div>
    </div>
  );
}

function StatChip({ label, value, tone }) {
  const map = {
    cyan:    "bg-cyan-500/20 text-cyan-100 ring-cyan-300/40",
    amber:   "bg-amber-500/20 text-amber-100 ring-amber-300/40",
    emerald: "bg-emerald-500/20 text-emerald-100 ring-emerald-300/40",
  };
  return (
    <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full ring-1 ${map[tone]}`}>
      <span className="text-[10px] font-mono uppercase tracking-widest">{label}</span>
      <span className="font-heading font-black text-base">{value}</span>
    </div>
  );
}
